/**
 * FieldLockEditor — backoffice panel.
 *
 * The administrator (admin OR partner_admin) uses this to configure
 * which fields on which pages require a master code for the subordinate
 * user to edit, plus the master code itself + lockout settings.
 *
 * Props:
 *   scope: "admin" | "partner"   — controls which API to call
 *   userId: subordinate user_id
 *   onSaved: optional callback
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, KeyRound, Save, AlertTriangle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const REGISTRY_ENDPOINT = {
    admin: "/admin/field-lock-registry",
    partner: "/partner/field-lock-registry",
};
const LOCKS_ENDPOINT = {
    admin: (uid) => `/admin/partner-users/${uid}/field-locks`,
    partner: (uid) => `/partner/team/${uid}/field-locks`,
};

export default function FieldLockEditor({ scope, userId, onSaved }) {
    const [registry, setRegistry] = useState(null);
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [newCode, setNewCode] = useState("");
    const [maxAttempts, setMaxAttempts] = useState(5);
    const [cooldownMin, setCooldownMin] = useState(10);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const reload = async () => {
        setBusy(true);
        try {
            const [reg, lk] = await Promise.all([
                api.get(REGISTRY_ENDPOINT[scope]),
                api.get(LOCKS_ENDPOINT[scope](userId)),
            ]);
            setRegistry(reg.data);
            setData(lk.data);
            setDraft(lk.data.field_lock_config || {});
            const s = lk.data.lock_settings || { max_attempts: 5, cooldown_min: 10 };
            setMaxAttempts(s.max_attempts);
            setCooldownMin(s.cooldown_min);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    useEffect(() => { reload();   }, [userId, scope]);

    const toggleField = (pageKey, fieldKey) => {
        setDraft((d) => {
            const cur = new Set(d?.[pageKey] || []);
            if (cur.has(fieldKey)) cur.delete(fieldKey); else cur.add(fieldKey);
            const next = { ...d, [pageKey]: Array.from(cur).sort() };
            if (next[pageKey].length === 0) delete next[pageKey];
            return next;
        });
    };

    const totalLocked = useMemo(() => Object.values(draft || {}).reduce((a, b) => a + b.length, 0), [draft]);

    const save = async () => {
        setBusy(true); setErr(""); setMsg("");
        try {
            const body = {
                field_lock_config: draft,
                lock_settings: { max_attempts: maxAttempts, cooldown_min: cooldownMin },
            };
            if (newCode) body.master_code = newCode;
            await api.patch(LOCKS_ENDPOINT[scope](userId), body);
            setMsg("Saved.");
            setNewCode("");
            await reload();
            onSaved?.();
            setTimeout(() => setMsg(""), 1500);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const clearCode = async () => {
        if (!window.confirm("Clear the master code? The user will lose access to locked fields entirely until a new code is set.")) return;
        setBusy(true); setErr("");
        try {
            await api.patch(LOCKS_ENDPOINT[scope](userId), { clear_master_code: true });
            await reload();
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    if (!registry || !data) {
        return <div className="text-xs text-slate-400 p-3">Loading field-lock config…</div>;
    }

    return (
        <div className="space-y-4 text-sm" data-testid={`fieldlock-editor-${scope}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-amber-600" />
                    <div>
                        <div className="font-medium">Edit-lock policy</div>
                        <div className="text-[11px] text-slate-500">
                            Gate sensitive fields behind a master code this user must enter before edits.
                        </div>
                    </div>
                </div>
                <div className="text-[11px] text-slate-500">
                    Master code: <strong className={data.has_master_code ? "text-emerald-600" : "text-rose-600"}>{data.has_master_code ? "Set" : "Not set"}</strong>
                </div>
            </div>

            {/* Per-page field checkboxes */}
            <div className="space-y-3">
                {Object.entries(registry).map(([pageKey, spec]) => {
                    const enabled = new Set(draft?.[pageKey] || []);
                    return (
                        <div key={pageKey} className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
                            <div className="text-xs font-medium text-slate-700 mb-2">{spec.label}</div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                {spec.fields.map((f) => (
                                    <label key={f.key} data-testid={`fl-${pageKey}-${f.key}`}
                                           className="flex items-center gap-2 text-xs cursor-pointer">
                                        <input type="checkbox" checked={enabled.has(f.key)}
                                               onChange={() => toggleField(pageKey, f.key)}
                                               className="rounded" />
                                        <span className={enabled.has(f.key) ? "font-medium text-slate-800" : "text-slate-600"}>{f.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="text-[11px] text-slate-500">
                Total locked fields: <strong>{totalLocked}</strong>
            </div>

            {/* Master code rotate */}
            <div className="border-t border-slate-200 pt-3 space-y-2">
                <div className="text-xs font-medium text-slate-700">Master code</div>
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="label">{data.has_master_code ? "Rotate (set a new code)" : "Set master code"}</label>
                        <input data-testid="fl-master-code" type="text" value={newCode}
                               onChange={(e) => setNewCode(e.target.value)}
                               className="input font-mono" placeholder="4–64 characters" />
                    </div>
                    {data.has_master_code && (
                        <button type="button" onClick={clearCode} disabled={busy}
                                className="btn-secondary px-3 py-2 rounded-md text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                            Clear
                        </button>
                    )}
                </div>
                <p className="text-[11px] text-slate-500">
                    Share this code with the user out-of-band (Signal/WhatsApp/in-person). Once set,
                    they enter it on any locked-field edit to start a 30-min sudo session.
                </p>
            </div>

            {/* Lockout settings */}
            <div className="border-t border-slate-200 pt-3 space-y-2">
                <div className="text-xs font-medium text-slate-700">Lockout policy</div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="label">Max failed attempts</label>
                        <input data-testid="fl-max-attempts" type="number" min={1} max={20} value={maxAttempts}
                               onChange={(e) => setMaxAttempts(parseInt(e.target.value || "1", 10))}
                               className="input" />
                    </div>
                    <div>
                        <label className="label">Cooldown (minutes)</label>
                        <input data-testid="fl-cooldown" type="number" min={1} max={1440} value={cooldownMin}
                               onChange={(e) => setCooldownMin(parseInt(e.target.value || "1", 10))}
                               className="input" />
                    </div>
                </div>
                {data.lock_failures?.locked_until && new Date(data.lock_failures.locked_until).getTime() > Date.now() && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        User is currently locked out until {new Date(data.lock_failures.locked_until).toLocaleString()}.
                    </div>
                )}
            </div>

            {err && <div data-testid="fl-err" className="text-rose-600 text-xs">{err}</div>}
            {msg && <div data-testid="fl-msg" className="text-emerald-600 text-xs">{msg}</div>}

            <div className="flex justify-end">
                <button data-testid="fl-save" disabled={busy} onClick={save}
                        className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save policy
                </button>
            </div>
        </div>
    );
}
